import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Settings, Mail, Bell, Users, Save, 
  ToggleLeft, ToggleRight, AlertTriangle, CheckCircle,
  Download, Upload, Database, FileJson, FileText, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

const TicketSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  
  // Backup/Restore state
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchEmailStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.getTicketSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const res = await api.getEmailStatus();
      setEmailStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch email status:', err);
    }
  };

  const handleToggle = async (field) => {
    const newValue = !settings[field];
    setSettings(prev => ({ ...prev, [field]: newValue }));
    
    try {
      await api.updateTicketSettings({ [field]: newValue });
      toast.success(`${field === 'email_polling_enabled' ? 'Email polling' : 'Auto-reply'} ${newValue ? 'enabled' : 'disabled'}`);
      
      if (field === 'email_polling_enabled' && newValue) {
        toast.info('Note: Backend restart required to start the email scheduler', { duration: 5000 });
      }
    } catch (err) {
      // Revert on error
      setSettings(prev => ({ ...prev, [field]: !newValue }));
      toast.error('Failed to update setting');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateTicketSettings({
        auto_reply_template: settings.auto_reply_template,
        it_staff: settings.it_staff,
        it_staff_emails: settings.it_staff_emails,
        default_assignee: settings.default_assignee,
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Backup function
  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.createBackup();
      const backup = res.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `helpdesk_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup created: ${backup.summary.users} users, ${backup.summary.managed_tickets} tickets, ${backup.summary.asset_metadata} assets`);
    } catch (err) {
      console.error('Backup failed:', err);
      toast.error('Failed to create backup');
    } finally {
      setBackingUp(false);
    }
  };

  // Restore function
  const handleRestoreClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Confirm before restore
    if (!window.confirm('This will REPLACE all existing data. Are you sure you want to restore from this backup?')) {
      e.target.value = '';
      return;
    }
    
    setRestoring(true);
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      const res = await api.restoreBackup(backupData);
      const results = res.data.results.collections;
      
      toast.success(`Restored: ${results.users || 0} users, ${results.managed_tickets || 0} tickets, ${results.asset_metadata || 0} assets`);
      
      // Refresh settings
      fetchSettings();
    } catch (err) {
      console.error('Restore failed:', err);
      toast.error('Failed to restore backup - invalid file format');
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  const handleStaffChange = (index, field, value) => {
    if (field === 'name') {
      const newStaff = [...settings.it_staff];
      newStaff[index] = value;
      setSettings(prev => ({ ...prev, it_staff: newStaff }));
    } else {
      const newEmails = [...settings.it_staff_emails];
      newEmails[index] = value;
      setSettings(prev => ({ ...prev, it_staff_emails: newEmails }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-gray-600" />
              <h1 className="font-bold text-slate-800">Settings</h1>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* FAN Reports Quick Access */}
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <FileText className="h-5 w-5" />
              FAN Reports
            </CardTitle>
            <CardDescription>
              Factory Analysis Network reporting for hardware issues
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/reports')}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open FAN Reports
            </Button>
          </CardContent>
        </Card>

        {/* Email Integration Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Integration
            </CardTitle>
            <CardDescription>
              Control email polling from shared mailbox and automatic replies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status indicator */}
            {emailStatus && (
              <div className={`p-3 rounded-lg ${emailStatus.configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-2">
                  {emailStatus.configured ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  )}
                  <span className={`text-sm font-medium ${emailStatus.configured ? 'text-green-700' : 'text-yellow-700'}`}>
                    {emailStatus.configured ? 'Azure credentials configured' : 'Azure credentials not configured'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mailbox: {emailStatus.mailbox}</p>
              </div>
            )}

            {/* Email Polling Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Email Polling</h3>
                <p className="text-sm text-gray-500">
                  Automatically check shared mailbox for new tickets
                </p>
                {settings.email_polling_enabled && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Backend restart required after enabling
                  </p>
                )}
              </div>
              <button
                onClick={() => handleToggle('email_polling_enabled')}
                className="focus:outline-none"
                data-testid="email-polling-toggle"
              >
                {settings.email_polling_enabled ? (
                  <ToggleRight className="h-10 w-10 text-green-500" />
                ) : (
                  <ToggleLeft className="h-10 w-10 text-gray-400" />
                )}
              </button>
            </div>

            {/* Auto Reply Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Auto-Reply</h3>
                <p className="text-sm text-gray-500">
                  Send automatic confirmation when ticket is created from email
                </p>
              </div>
              <button
                onClick={() => handleToggle('auto_reply_enabled')}
                className="focus:outline-none"
                data-testid="auto-reply-toggle"
              >
                {settings.auto_reply_enabled ? (
                  <ToggleRight className="h-10 w-10 text-green-500" />
                ) : (
                  <ToggleLeft className="h-10 w-10 text-gray-400" />
                )}
              </button>
            </div>

            {/* Auto Reply Template */}
            {settings.auto_reply_enabled && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Auto-Reply Template
                </label>
                <Textarea
                  value={settings.auto_reply_template || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, auto_reply_template: e.target.value }))}
                  className="min-h-[120px] font-mono text-sm"
                  placeholder="Use {ticket_number} and {subject} as placeholders"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Available placeholders: {'{ticket_number}'}, {'{subject}'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* IT Staff */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              IT Staff
            </CardTitle>
            <CardDescription>
              Staff members who can be assigned tickets and receive CC on communications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.it_staff?.map((name, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Name</label>
                  <Input
                    value={name}
                    onChange={(e) => handleStaffChange(idx, 'name', e.target.value)}
                    placeholder="Staff name"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email</label>
                  <Input
                    value={settings.it_staff_emails?.[idx] || ''}
                    onChange={(e) => handleStaffChange(idx, 'email', e.target.value)}
                    placeholder="staff@company.com"
                    type="email"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Default Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Defaults
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Default Assignee
              </label>
              <select
                value={settings.default_assignee || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, default_assignee: e.target.value || null }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {settings.it_staff?.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                New tickets from email will be assigned to this person
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Backup & Restore */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-700 flex items-center gap-2">
              <Database className="h-5 w-5" />
              Backup & Restore
            </CardTitle>
            <CardDescription>
              Export all data or restore from a backup file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium">Create Backup</h4>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Download a JSON file with all users, tickets, assets, and settings.
                </p>
                <Button 
                  onClick={handleBackup} 
                  disabled={backingUp}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="backup-btn"
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  {backingUp ? 'Creating...' : 'Download Backup'}
                </Button>
              </div>
              
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Upload className="h-5 w-5 text-orange-600" />
                  <h4 className="font-medium">Restore Backup</h4>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Upload a backup file to restore all data. This will replace existing data.
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".json"
                  className="hidden"
                />
                <Button 
                  onClick={handleRestoreClick} 
                  disabled={restoring}
                  variant="outline"
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="restore-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {restoring ? 'Restoring...' : 'Upload & Restore'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Restore will overwrite ALL existing data. Make a backup first!
            </p>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Caution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              <strong>Email Polling:</strong> When enabled, the system will automatically create tickets from emails sent to the shared mailbox. 
              Make sure this is what you want before enabling.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Auto-Reply:</strong> When enabled, automatic confirmation emails will be sent to anyone who emails the help desk.
              This could contact old ticket requesters if you're importing historical data.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TicketSettingsPage;
